/**
 * Kanonisk `page_location` för analytics.
 *
 * Tre problem den löser:
 *
 * 1. TanStack Routers `location.href` är relativ (`/skolor?q=nacka`). GA4
 *    förväntar sig en absolut URL i `page_location` — annars grupperas
 *    sidorna fel i rapporterna.
 * 2. Default-värden i sökschemat (sort=name, page=1, tomma filter) hamnar
 *    ibland i adressfältet och splittrar samma sida på flera rader i GA4.
 *    De rensas bort så att `/skolor` och `/skolor?sort=name&page=1` räknas
 *    som samma sida.
 * 3. Maskering av engångstoken sker på samma ställe, så ingen anropsväg kan
 *    glömma den.
 */
import { redactUrl } from "./redact-url";
import { PUBLIC_SITE_URL } from "./site-hosts";

/** Parametrar som är default och därför aldrig ska synas i rapporten. */
const DEFAULTS: Record<string, Record<string, string>> = {
  "/skolor": {
    q: "",
    type: "",
    huvudman: "",
    lan: "",
    kommun: "",
    sort: "name",
    utvalda: "false",
    karta: "false",
    page: "1",
  },
  "/jobb": { q: "", lan: "", kommun: "", roll: "", omfattning: "", page: "1" },
};

function origin(): string {
  if (typeof window !== "undefined" && window.location?.origin) return window.location.origin;
  return PUBLIC_SITE_URL;
}

/**
 * @param href Relativ eller absolut URL. Utan argument används adressfältet.
 */
export function pageLocation(href?: string): string {
  try {
    const raw = href ?? (typeof window !== "undefined" ? window.location.href : "/");
    const url = new URL(raw, origin());

    const defaults = DEFAULTS[url.pathname];
    for (const key of Array.from(new Set(url.searchParams.keys()))) {
      const value = url.searchParams.get(key) ?? "";
      if (value === "" || (defaults && defaults[key] === value)) {
        url.searchParams.delete(key);
      }
    }
    // Stabil ordning: samma filterkombination ger samma sträng oavsett i
    // vilken ordning användaren klickade.
    url.searchParams.sort();

    return redactUrl(url.toString());
  } catch {
    return redactUrl(href ?? "/");
  }
}
